import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useTheme } from "../context/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import { FiTrash2, FiEdit,FiMail, FiCheckCircle, FiXCircle, FiSun, FiMoon, FiMenu, FiX, FiDollarSign, FiSmartphone, FiCreditCard, FiMessageSquare, FiMap } from "react-icons/fi";
import { AiFillHeart } from "react-icons/ai";
import PropTypes from "prop-types";
import Chatbot from "../components/Chatbot";
import ProfileModal from "../components/ProfileModal";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useAuth } from "../context/AuthContext"; // Adjust path if needed



// Fallback image
const FALLBACK_IMAGE = "/zoomo-mascot.png";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};
// const { user } = useAuth();


// Toast Component
const Toast = ({ message, type = "success", onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = {
    success: "bg-[var(--primary)]",
    error: "bg-red-600",
    info: "bg-blue-500",
  }[type];

  return (
    <motion.div
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      className={`fixed top-4 right-4 ${bgColor} text-[var(--text-light)] px-4 py-3 rounded-2xl shadow-2xl z-[1000] flex items-center gap-2 text-sm font-semibold`}
    >
      {type === "success" && <FiCheckCircle className="w-5 h-5" />}
      {type === "error" && <FiXCircle className="w-5 h-5" />}
      {message}
    </motion.div>
  );
};

Toast.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["success", "error", "info"]),
  onClose: PropTypes.func.isRequired,
};

// Dummy Menus
const dummyMenus = {
  1: { name: "I Love Pizza, Jourian" },
  2: { name: "Sharma Fast Food" },
  3: { name: "Coffee Express" },
  5: { name: "Moonlight Cafe" },
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-red-600 text-lg font-semibold">Something went wrong. Please try refreshing the page.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const CartPage = () => {
  const { user } = useAuth();


  const { cart = [], removeFromCart, clearCart, updateQuantity, checkout, addToCart } = useCart();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();

  const [address, setAddress] = useState("");
  const debouncedAddress = useDebounce(address, 300);
  const [saveAddress, setSaveAddress] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [trackingStage, setTrackingStage] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [upiId, setUpiId] = useState("");
  const debouncedUpiId = useDebounce(upiId, 300);
  const [promoCode, setPromoCode] = useState("");
  const debouncedPromoCode = useDebounce(promoCode, 300);
  const [discount, setDiscount] = useState(0);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [tipAmount, setTipAmount] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [deliveryPartner, setDeliveryPartner] = useState(null);
  const [estimatedDeliveryTime, setEstimatedDeliveryTime] = useState(null);
  const [savedItems, setSavedItems] = useState(() => {
    const saved = localStorage.getItem("savedItems");
    return saved ? JSON.parse(saved) : [];
  });
  const [showClearCartModal, setShowClearCartModal] = useState(false);
  const [showClearSavedModal, setShowClearSavedModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [editItem, setEditItem] = useState(null);
  const [customizations, setCustomizations] = useState({ toppings: {}, spiceLevel: "" });
  const [addressError, setAddressError] = useState("");
  const [upiError, setUpiError] = useState("");
  const [promoError, setPromoError] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);

  // Load user avatar from localStorage
  const savedProfile = JSON.parse(localStorage.getItem("profile") || "{}");
  const userAvatar = savedProfile.avatar || "/zoomo-mascot.png";

  useEffect(() => {
    localStorage.setItem("savedItems", JSON.stringify(savedItems));
  }, [savedItems]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const subtotal = useMemo(() => cart.reduce((sum, item) => sum + item.price * item.quantity, 0), [cart]);
  const tax = useMemo(() => subtotal * 0.05, [subtotal]);
  const deliveryFee = 20;
  const total = useMemo(
    () => subtotal + tax + deliveryFee - discount + Number(tipAmount || customTip || 0),
    [subtotal, tax, discount, tipAmount, customTip]
  );

  const validPromoCodes = useMemo(() => ({
    ZOOMO20: 0.2,
    FIRST10: 0.1,
    FREESHIP: 20,
  }), []);

  const generateRealisticOrderId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `ZOOMO-${timestamp}-${random}`;
  };

  const handleCheckout = useCallback(async () => {
    setIsCheckingOut(true);
  
    try {
      const realisticOrderId = generateRealisticOrderId();
  
      const order = {
        userId: user?.uid || "guest",
        items: cart,
        total,
        address: debouncedAddress,
        paymentMethod,
        upiId: debouncedUpiId,
        instructions: specialInstructions,
        tip: Number(tipAmount || customTip || 0),
        promo: appliedPromo,
        status: "pending",
        createdAt: serverTimestamp(),
      };
  
      const docRef = await addDoc(collection(db, "orders"), order);
  
      setOrderPlaced(true);
      setCurrentOrderId(realisticOrderId);
  
      setToast({
        message: `Order #${realisticOrderId} placed successfully!`,
        type: "success",
      });
  
      clearCart();
      navigate(`/track-order/${docRef.id}`);
    } catch (error) {
      console.error("Checkout error", error);
      setToast({ message: "Order failed. Try again.", type: "error" });
    } finally {
      setIsCheckingOut(false);
    }
  }, [
    user,
    cart,
    total,
    debouncedAddress,
    paymentMethod,
    debouncedUpiId,
    specialInstructions,
    tipAmount,
    customTip,
    appliedPromo,
    navigate,
    clearCart,
  ]);
  

  
  
  const handleApplyPromoCode = useCallback(() => {
    setPromoError("");
    const code = debouncedPromoCode.toUpperCase().trim();
    if (appliedPromo === code) {
      setPromoError("This promo code is already applied.");
      setToast({ message: "Promo code already applied.", type: "error" });
      return;
    }
    if (validPromoCodes[code]) {
      const discountValue = validPromoCodes[code] < 1 ? subtotal * validPromoCodes[code] : validPromoCodes[code];
      setDiscount(discountValue);
      setAppliedPromo(code);
      setToast({ message: `Promo code ${code} applied! Saved ₹${discountValue.toFixed(2)}.`, type: "success" });
    } else {
      setPromoError("Invalid promo code.");
      setToast({ message: "Invalid promo code.", type: "error" });
    }
  }, [debouncedPromoCode, subtotal, validPromoCodes, appliedPromo]);

  const handleTipSelect = useCallback((amount) => {
    setTipAmount(amount);
    setCustomTip("");
    setToast({ message: `Tip set to ₹${amount}`, type: "success" });
  }, []);

  const handleCustomTipChange = useCallback((e) => {
    const value = e.target.value;
    if (value >= 0 || value === "") {
      setCustomTip(value);
      setTipAmount(0);
    } else {
      setToast({ message: "Tip amount cannot be negative.", type: "error" });
    }
  }, []);

  const handleSaveForLater = useCallback(
    (item) => {
      setSavedItems((prev) => [...prev, item]);
      removeFromCart(item.id);
      setToast({ message: `${item.name} saved for later.`, type: "success" });
    },
    [removeFromCart]
  );

  const handleAddBackToCart = useCallback(
    (item, index) => {
      setSavedItems((prev) => prev.filter((_, i) => i !== index));
      addToCart(item, item.quantity, item.customizations, item.schedule);
      setToast({ message: `${item.name} added back to cart.`, type: "success" });
    },
    [addToCart]
  );

  const handleClearSavedItems = useCallback(() => {
    setSavedItems([]);
    localStorage.removeItem("savedItems");
    setShowClearSavedModal(false);
    setToast({ message: "All saved items cleared.", type: "success" });
  }, []);

  const handleEditItem = useCallback((item) => {
    setEditItem(item);
    setCustomizations(item.customizations || { toppings: {}, spiceLevel: "" });
  }, []);

  const handleSaveCustomizations = useCallback(() => {
    if (!editItem) return;
    updateQuantity(editItem.id, 0);
    addToCart(
      { id: editItem.id, name: editItem.name, price: editItem.price, img: editItem.img, restaurantId: editItem.restaurantId },
      editItem.quantity,
      customizations,
      editItem.schedule
    );
    setEditItem(null);
    setToast({ message: `${editItem.name} customizations updated.`, type: "success" });
  }, [editItem, customizations, updateQuantity, addToCart]);

  const handleInstructionSuggestion = useCallback((instruction) => {
    setSpecialInstructions((prev) => (prev ? `${prev}, ${instruction}` : instruction));
    setToast({ message: `Added instruction: ${instruction}`, type: "info" });
  }, []);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (newsletterEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setToast({ message: "Subscribed to newsletter!", type: "success" });
      setNewsletterEmail("");
    } else {
      setToast({ message: "Invalid email address.", type: "error" });
    }
  };

  const handleSetOnMap = () => {
    // Simulate map integration (e.g., Google Maps API)
    console.log("Set on Map clicked - Implement map integration here");
    setToast({ message: "Set on Map functionality coming soon!", type: "info" });
  };

  if (!cart) {
    return (
      <div className={`min-h-screen flex flex-col font-montserrat ${darkMode ? "bg-[var(--bg-dark)] text-[var(--text-dark)]" : "bg-[var(--bg-light)] text-[var(--text-light)]"} text-center p-6`}>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-red-600">Error: Cart data is missing</h1>
        <p className="text-sm sm:text-base mt-4">Please check CartContext or try refreshing the page.</p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className={`min-h-screen flex flex-col font-montserrat ${darkMode ? "bg-[var(--bg-dark)] text-[var(--text-dark)]" : "bg-[var(--bg-light)] text-[var(--text-light)]"} relative overflow-x-hidden transition-colors duration-300`}>
        <style jsx global>{`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');
          :root {
            --primary: #00b86b;
            --primary-dark: #007a4b;
            --secondary: #f5f5f5;
            --bg-light: #fafafa;
            --bg-dark: #1a1a1a;
            --text-light: #000000;
            --text-dark: #ffffff;
            --card-bg-light: #ffffff;
            --card-bg-dark: #222;
            --border-light: #e6f0ff;
            --border-dark: #333;
          }
          .font-montserrat { font-family: 'Montserrat', sans-serif; }
          .custom-scrollbar::-webkit-scrollbar { width: 6px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: ${darkMode ? 'var(--border-dark)' : 'var(--border-light)'}; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: var(--primary); border-radius: 10px; }
          .animate-slideIn { animation: slideIn 0.5s ease-in-out; }
          .animate-fadeIn { animation: fadeIn 0.5s ease-out; }
          @keyframes slideIn {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .gradient-button {
            background: linear-gradient(135deg, var(--primary), var(--primary-dark));
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }
          .gradient-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 20px rgba(0, 184, 107, 0.4);
          }
          .gradient-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .card-hover:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 24px rgba(0, 184, 107, 0.2);
          }
          .section-container {
            background: ${darkMode ? 'var(--card-bg-dark)' : 'var(--card-bg-light)'};
            border-radius: 16px;
            padding: 2rem;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            margin-bottom: 2rem;
          }
          button:focus, input:focus, select:focus, textarea:focus {
            outline: none;
            box-shadow: 0 0 0 3px rgba(0, 184, 107, 0.3);
          }
          .floating-action-button {
            position: fixed;
            right: 24px;
            z-index: 1000;
            background: var(--primary);
            color: var(--text-light);
            border-radius: 50%;
            width: 64px;
            height: 64px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
            transition: transform 0.3s ease;
          }
          .floating-action-button:hover {
            transform: scale(1.1);
          }
          .floating-support {
            bottom: 80px;
          }
          .input-field {
            background: ${darkMode ? 'var(--border-dark)' : 'var(--border-light)'};
            border: 1px solid ${darkMode ? 'var(--border-dark)' : 'var(--border-light)'};
            color: ${darkMode ? 'var(--text-dark)' : 'var(--text-light)'};
            padding: 12px 16px;
            border-radius: 12px;
            width: 100%;
            font-size: 14px;
            transition: all 0.3s ease;
          }
          .input-field:focus {
            border-color: var(--primary);
            box-shadow: 0 0 0 3px rgba(0, 184, 107, 0.2);
          }
          .payment-button {
            background: ${darkMode ? 'var(--card-bg-dark)' : 'var(--border-light)'};
            color: ${darkMode ? 'var(--text-dark)' : 'var(--text-light)'};
            padding: 10px 20px;
            border-radius: 10px;
            font-size: 14px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.3s ease;
          }
          .payment-button:hover {
            background: ${darkMode ? 'var(--border-dark)' : '#d3e3ff'};
            box-shadow: 0 0 10px rgba(0, 184, 107, 0.2);
          }
          .payment-button.active {
            background: var(--primary);
            color: var(--text-light);
          }
          .loader {
            border: 4px solid #f3f3f3;
            border-top: 4px solid var(--primary);
            border-radius: 50%;
            width: 24px;
            height: 24px;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @media (max-width: 768px) {
            .grid-cols-3 { grid-template-columns: 1fr; }
            .px-6 { padding-left: 1rem; padding-right: 1rem; }
            .text-4xl { font-size: 2rem; }
            .text-3xl { font-size: 1.5rem; }
          }
          @media (max-width: 640px) {
            .grid-cols-2 { grid-template-columns: 1fr; }
            .px-4 { padding-left: 0.75rem; padding-right: 0.75rem; }
          }
        `}</style>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        {/* Header */}
        <motion.header
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
          className={`fixed top-0 left-0 w-full py-3 px-4 sm:px-6 flex justify-between items-center z-50 ${darkMode ? "bg-[var(--bg-dark)]/95" : "bg-[var(--bg-light)]/95"} backdrop-blur-md transition-all duration-300 ${isScrolled ? "shadow-lg" : "shadow-none"}`}
          role="banner"
        >
          <div className="flex items-center gap-3">
            <motion.img
              src="/zoomo-logo.png"
              alt="Zoomo Eats Logo"
              className="w-10 h-10"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.3 }}
              onError={(e) => (e.target.src = FALLBACK_IMAGE)}
            />
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight uppercase">
              ZOOMO <span className="text-[var(--primary)]">EATS</span>
            </h2>
          </div>
          <nav className="hidden md:flex items-center gap-4" role="navigation">
            <motion.button
              onClick={toggleDarkMode}
              whileHover={{ scale: 1.1, rotate: 15 }}
              whileTap={{ scale: 0.9 }}
              className="p-2 rounded-full bg-[var(--primary)] text-[var(--text-light)] hover:bg-[var(--primary-dark)] transition-all duration-300"
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? <FiSun className="w-6 h-6" /> : <FiMoon className="w-6 h-6" />}
            </motion.button>
            <motion.button
              onClick={() => setProfileOpen(true)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="relative group"
              aria-label="Open user profile modal"
            >
              <img
                src={userAvatar}
                alt="User Avatar"
                className="w-10 h-10 rounded-full object-cover border-2 border-[var(--primary)] group-hover:scale-110 transition-transform duration-300"
                onError={(e) => (e.target.src = FALLBACK_IMAGE)}
              />
            </motion.button>
          </nav>
          <motion.button
            className="md:hidden text-[var(--primary)]"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <FiX className="w-8 h-8" /> : <FiMenu className="w-8 h-8" />}
          </motion.button>
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.3 }}
                className={`fixed top-0 left-0 w-full h-full ${darkMode ? "bg-[var(--bg-dark)]" : "bg-[var(--bg-light)]"} md:hidden flex flex-col items-center justify-center z-40`}
              >
                <motion.button
                  onClick={() => setIsMenuOpen(false)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="absolute top-4 right-4 text-[var(--primary)]"
                  aria-label="Close menu"
                >
                  <FiX className="w-10 h-10" />
                </motion.button>
                <div className="flex flex-col items-center gap-8">
                  <motion.button
                    onClick={() => { setProfileOpen(true); setIsMenuOpen(false); }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="text-2xl font-semibold text-[var(--primary)] py-4 px-8 rounded-lg hover:bg-[var(--primary)]/10 transition-all"
                    aria-label="Open user profile"
                  >
                    Profile
                  </motion.button>
                  <motion.button
                    onClick={toggleDarkMode}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="text-2xl font-semibold text-[var(--primary)] py-4 px-8 rounded-lg hover:bg-[var(--primary)]/10 transition-all flex items-center gap-2"
                    aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                  >
                    {darkMode ? <FiSun className="w-6 h-6" /> : <FiMoon className="w-6 h-6" />}
                    {darkMode ? "Light Mode" : "Dark Mode"}
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.header>

        {/* Floating Support Button */}
        <motion.div
          className="floating-action-button floating-support"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setShowSupport(true)}
          aria-label="Open support chatbot"
        >
          <FiMessageSquare className="w-6 h-6" />
        </motion.div>

        {/* Profile Modal */}
        <ProfileModal
          darkMode={darkMode}
          setToastMessage={setToast}
          toggleFavouriteDish={() => {}}
          favourites={[]}
          isOpen={profileOpen}
          onClose={() => setProfileOpen(false)}
        />

        <main className="pt-24 px-4 md:px-6 pb-16 max-w-7xl mx-auto" role="main">
          <section className="section-container animate-slideIn">
            <h1 className="text-3xl md:text-4xl font-extrabold text-center mb-6 text-[var(--primary)]">
              Your Cart
            </h1>

            {orderPlaced ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto text-center"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-[var(--primary)] mb-4">Order Placed Successfully!</h2>
                <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"} mb-6`}>
                  Your order #{currentOrderId} is being processed. Track its status below:
                </p>
                <div className="relative mb-6">
                  <div className="flex justify-between items-center">
                    {["Order Placed", "Preparing", "On the way", "Delivered"].map((stage, i) => (
                      <div key={i} className="flex-1 text-center relative">
                        <motion.div
                          animate={{ scale: trackingStage >= i ? 1.1 : 1 }}
                          className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center transition-all duration-500 ${
                            trackingStage >= i
                              ? "bg-[var(--primary)] text-[var(--text-light)] shadow-lg"
                              : darkMode
                              ? "bg-[var(--border-dark)] text-gray-400"
                              : "bg-[var(--border-light)] text-gray-500"
                          }`}
                        >
                          {trackingStage > i ? <FiCheckCircle /> : i + 1}
                        </motion.div>
                        <p className="text-xs mt-2 font-medium">{stage}</p>
                      </div>
                    ))}
                  </div>
                  <div className={`absolute top-5 left-0 right-0 h-1 ${darkMode ? "bg-gray-700" : "bg-gray-300"}`}>
                    <motion.div
                      className="h-full bg-[var(--primary)]"
                      initial={{ width: 0 }}
                      animate={{ width: `${(trackingStage / 3) * 100}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
                {deliveryPartner && estimatedDeliveryTime && (
                  <div className="mb-6">
                    <p className="text-base font-semibold text-[var(--primary)]">
                      Estimated Delivery: {estimatedDeliveryTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                    <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                      Assigned to: {deliveryPartner.name} ({deliveryPartner.phone})
                    </p>
                  </div>
                )}
                <div className="flex flex-wrap justify-center gap-3">
                  <motion.button
                    onClick={() => navigate(`/track-order/${currentOrderId}`)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="gradient-button text-[var(--text-light)] px-6 py-2 rounded-lg text-sm font-semibold shadow-md"
                    aria-label="Track order"
                  >
                    Track Order
                  </motion.button>
                  <motion.button
                    onClick={() => navigate("/restaurants?mode=delivery")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="gradient-button text-[var(--text-light)] px-6 py-2 rounded-lg text-sm font-semibold shadow-md"
                    aria-label="Order more"
                  >
                    Order More
                  </motion.button>
                  <motion.button
                    onClick={() => navigate("/orders")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="gradient-button text-[var(--text-light)] px-6 py-2 rounded-lg text-sm font-semibold shadow-md"
                    aria-label="View orders"
                  >
                    View Orders
                  </motion.button>
                </div>
              </motion.div>
            ) : cart.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
                <p className={`text-base ${darkMode ? "text-gray-400" : "text-gray-600"} mb-6`}>
                  Your cart is empty.
                </p>
                <motion.button
                  onClick={() => navigate("/restaurants?mode=delivery")}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="gradient-button text-[var(--text-light)] px-6 py-3 rounded-lg text-sm font-semibold shadow-md"
                  aria-label="Explore restaurants"
                >
                  Explore Restaurants
                </motion.button>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-4">
                  {isCheckingOut && (
                    <div className="text-center mb-4">
                      <p className="text-base font-semibold text-[var(--primary)]">Processing your order...</p>
                      <div className="loader mx-auto mt-2" />
                    </div>
                  )}
                  {Array.from(new Set(cart.map((item) => item.restaurantId || "default"))).map((restaurantId) => {
                    const restaurantItems = cart.filter((item) => (item.restaurantId || "default") === restaurantId);
                    return (
                      <div key={restaurantId} className="space-y-3">
                        <h3 className="text-lg font-semibold text-[var(--primary)]">
                          {restaurantId === "default" ? "Your Order" : dummyMenus[restaurantId]?.name || "Restaurant"}
                        </h3>
                        {restaurantItems.map((item) => (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`rounded-xl shadow-lg card-hover transition-all duration-300 flex flex-col sm:flex-row justify-between items-center bg-[var(--card-bg-light)] dark:bg-[var(--card-bg-dark)] p-4`}
                          >
                            <div className="flex items-center gap-4 w-full sm:w-auto">
                              <img
                                src={item.img || FALLBACK_IMAGE}
                                alt={item.name}
                                className="w-20 h-20 object-cover rounded-lg shadow"
                                onError={(e) => (e.target.src = FALLBACK_IMAGE)}
                                loading="lazy"
                              />
                              <div className="flex-1">
                                <h2 className="text-base font-semibold truncate">{item.name}</h2>
                                <p className="text-[var(--primary)] font-semibold text-sm">
                                  ₹{(item.price * item.quantity).toFixed(2)}
                                </p>
                                <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                                  Quantity: {item.quantity}
                                </p>
                                {Object.keys(item.customizations || {}).length > 0 && (
                                  <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                                    Customizations: {Object.entries(item.customizations).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`).join(", ")}
                                  </p>
                                )}
                                {item.schedule && (
                                  <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                                    Scheduled for: {new Date(item.schedule).toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-2 sm:mt-0">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => {
                                  const newQuantity = Math.max(0, item.quantity - 1);
                                  updateQuantity(item.id, newQuantity);
                                  if (newQuantity > 0) {
                                    setToast({ message: `Updated ${item.name} quantity to ${newQuantity}`, type: "info" });
                                  } else {
                                    setToast({ message: `${item.name} removed from cart.`, type: "info" });
                                  }
                                }}
                                className={`p-2 rounded-full text-sm font-semibold transition-all ${darkMode ? "bg-[var(--border-dark)] text-[var(--text-dark)] hover:bg-[#444]" : "bg-[var(--border-light)] text-[var(--text-light)] hover:bg-[#d3e3ff]"}`}
                                aria-label={`Decrease quantity of ${item.name}`}
                              >
                                -
                              </motion.button>
                              <span className="text-sm font-medium">{item.quantity}</span>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => {
                                  const newQuantity = item.quantity + 1;
                                  updateQuantity(item.id, newQuantity);
                                  setToast({ message: `Updated ${item.name} quantity to ${newQuantity}`, type: "info" });
                                }}
                                className="gradient-button text-[var(--text-light)] p-2 rounded-full shadow-md"
                                aria-label={`Increase quantity of ${item.name}`}
                              >
                                +
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleEditItem(item)}
                                className="gradient-button text-[var(--text-light)] p-2 rounded-full transition-all"
                                aria-label={`Edit ${item.name}`}
                              >
                                <FiEdit className="w-4 h-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleSaveForLater(item)}
                                className="gradient-button text-[var(--text-light)] p-2 rounded-full"
                                aria-label={`Save ${item.name} for later`}
                              >
                                Save
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => {
                                  removeFromCart(item.id);
                                  setToast({ message: `${item.name} removed from cart.`, type: "info" });
                                }}
                                className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full"
                                aria-label={`Remove ${item.name} from cart`}
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </motion.button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    );
                  })}
                </div>

                <div className="md:col-span-1">
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="sticky top-24 rounded-lg shadow-lg bg-[var(--card-bg-light)] dark:bg-[var(--card-bg-dark)] p-4"
                  >
                    <h3 className="text-lg font-semibold text-center mb-4 text-[var(--primary)]">Order Summary</h3>
                    <div className="mb-4">
                      <div className="flex justify-between items-center">
                        {["Address", "Payment", "Confirmation"].map((step, i) => (
                          <div key={i} className="flex-1 text-center">
                            <motion.div
                              animate={{ scale: checkoutStep === i + 1 ? 1.1 : 1 }}
                              className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center text-sm ${
                                checkoutStep > i + 1
                                  ? "bg-[var(--primary)] text-[var(--text-light)]"
                                  : checkoutStep === i + 1
                                  ? "bg-[var(--primary)] text-[var(--text-light)]"
                                  : darkMode
                                  ? "bg-[var(--border-dark)] text-gray-400"
                                  : "bg-[var(--border-light)] text-gray-500"
                              }`}
                            >
                              {checkoutStep > i + 1 ? <FiCheckCircle /> : i + 1}
                            </motion.div>
                            <p className="text-xs mt-1">{step}</p>
                          </div>
                        ))}
                      </div>
                      <div className="h-1 bg-gray-300 dark:bg-gray-700">
                        <motion.div
                          className="h-full bg-[var(--primary)]"
                          initial={{ width: 0 }}
                          animate={{ width: `${((checkoutStep - 1) / 2) * 100}%` }}
                          transition={{ duration: 0.5 }}
                        />
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between font-semibold">
                        <span>Subtotal:</span>
                        <span>₹{subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Tax (5%):</span>
                        <span>₹{tax.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Delivery Fee:</span>
                        <span>₹{deliveryFee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Discount:</span>
                        <span className="text-[var(--primary)]">-₹{discount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Tip:</span>
                        <span className="text-[var(--primary)]">+₹{(Number(tipAmount || customTip || 0)).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-base border-t border-gray-300 dark:border-gray-700 pt-2">
                        <span>Total:</span>
                        <span className="text-[var(--primary)]">₹{total.toFixed(2)}</span>
                      </div>
                    </div>

                    {checkoutStep === 1 && (
                      <>
                        <div className="mt-4">
                          <label htmlFor="address-input" className="block text-sm font-medium mb-1">
                            Delivery Address
                          </label>
                          <div className="flex gap-2">
                            <input
                              id="address-input"
                              type="text"
                              placeholder="Enter delivery address"
                              value={address}
                              onChange={(e) => {
                                setAddress(e.target.value);
                                setSaveAddress(true);
                                setAddressError("");
                              }}
                              className="input-field flex-1"
                              aria-label="Enter delivery address"
                              maxLength={500}
                            />
                            <motion.button
                              onClick={handleSetOnMap}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="gradient-button text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold shadow-md"
                              aria-label="Set address on map"
                            >
                              <FiMap className="w-5 h-5" />
                            </motion.button>
                          </div>
                          {addressError && <p className="text-red-600 text-xs mt-1">{addressError}</p>}
                          <label className="flex items-center gap-2 mt-2">
                            <input
                              type="checkbox"
                              checked={saveAddress}
                              onChange={(e) => setSaveAddress(e.target.checked)}
                              className="h-4 w-4 text-[var(--primary)] focus:ring-[var(--primary)]"
                              aria-label="Save this address"
                            />
                            <span className="text-sm">Save this address</span>
                          </label>
                        </div>
                        <motion.button
                          onClick={handleCheckout}
                          disabled={isCheckingOut}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="gradient-button w-full text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold shadow-md mt-4"
                          aria-label="Proceed to payment"
                        >
                          Next: Payment
                        </motion.button>
                      </>
                    )}

                    {checkoutStep === 2 && (
                      <>
                        <div className="mt-4">
                          <label className="block text-sm font-medium mb-1">Payment Method</label>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { value: "Cash", label: "Cash", icon: FiDollarSign },
                              { value: "UPI", label: "UPI", icon: FiSmartphone },
                              { value: "Credit Card", label: "Credit Card", icon: FiCreditCard },
                              { value: "Debit Card", label: "Debit Card", icon: FiCreditCard },
                            ].map((method) => {
                              const Icon = method.icon;
                              return (
                                <motion.button
                                  key={method.value}
                                  onClick={() => setPaymentMethod(method.value)}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className={`flex-1 payment-button ${paymentMethod === method.value ? "active" : ""}`}
                                  aria-label={`Select ${method.label} payment method`}
                                >
                                  <Icon className="w-5 h-5" />
                                  <span>{method.label}</span>
                                </motion.button>
                              );
                            })}
                          </div>
                        </div>
                        {paymentMethod === "UPI" && (
                          <div className="mt-4">
                            <label htmlFor="upi-id-input" className="block text-sm font-medium mb-1">
                              UPI ID
                            </label>
                            <input
                              id="upi-id-input"
                              type="text"
                              placeholder="example@upi"
                              value={upiId}
                              onChange={(e) => {
                                setUpiId(e.target.value);
                                setUpiError("");
                              }}
                              className="input-field"
                              aria-label="Enter UPI ID"
                            />
                            {upiError && <p className="text-red-600 text-xs mt-1">{upiError}</p>}
                          </div>
                        )}
                        <motion.button
                          onClick={handleCheckout}
                          disabled={isCheckingOut}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="gradient-button w-full text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold shadow-md mt-4"
                          aria-label="Proceed to confirmation"
                        >
                          Next: Confirmation
                        </motion.button>
                      </>
                    )}

                    {checkoutStep === 3 && (
                      <>
                        <div className="mt-4">
                          <label htmlFor="promo-code-input" className="block text-sm font-medium mb-1">
                            Promo Code
                          </label>
                          <div className="flex gap-2">
                            <input
                              id="promo-code-input"
                              type="text"
                              placeholder="Enter promo code (e.g., ZOOMO20)"
                              value={promoCode}
                              onChange={(e) => {
                                setPromoCode(e.target.value);
                                setPromoError("");
                              }}
                              className="input-field flex-1"
                              aria-label="Enter promo code"
                            />
                            <motion.button
                              onClick={handleApplyPromoCode}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="gradient-button text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold shadow-md"
                              aria-label="Apply promo code"
                            >
                              Apply
                            </motion.button>
                          </div>
                          {promoError && <p className="text-red-600 text-xs mt-1">{promoError}</p>}
                        </div>

                        <div className="mt-4">
                          <label className="block text-sm font-medium mb-1">Add a Tip</label>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {[20, 50, 100].map((amount) => (
                              <motion.button
                                key={amount}
                                onClick={() => handleTipSelect(amount)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tipAmount === amount ? "gradient-button text-[var(--text-light)]" : darkMode ? "bg-[var(--border-dark)] text-[var(--text-dark)] hover:bg-[#444]" : "bg-[var(--border-light)] text-[var(--text-light)] hover:bg-[#d3e3ff]"}`}
                                aria-label={`Set tip to ₹${amount}`}
                              >
                                ₹{amount}
                              </motion.button>
                            ))}
                            <input
                              type="number"
                              placeholder="Custom"
                              value={customTip}
                              onChange={handleCustomTipChange}
                              className="input-field flex-1"
                              min="0"
                              aria-label="Enter custom tip amount"
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <label htmlFor="instructions-textarea" className="block text-sm font-medium mb-1">
                            Special Instructions
                          </label>
                          <textarea
                            id="instructions-textarea"
                            placeholder="E.g., Please ring the bell, no onions..."
                            value={specialInstructions}
                            onChange={(e) => setSpecialInstructions(e.target.value)}
                            className="input-field"
                            rows="3"
                            aria-label="Enter special instructions"
                          />
                          <div className="flex flex-wrap gap-2 mt-2">
                            {["No onions", "Extra spicy", "Contactless delivery"].map((suggestion) => (
                              <motion.button
                                key={suggestion}
                                onClick={() => handleInstructionSuggestion(suggestion)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className={`px-4 py-2 rounded-lg text-xs ${darkMode ? "bg-[var(--border-dark)] text-[var(--text-dark)] hover:bg-[#444]" : "bg-[var(--border-light)] text-[var(--text-light)] hover:bg-[#d3e3ff]"}`}
                                aria-label={`Add instruction: ${suggestion}`}
                              >
                                {suggestion}
                              </motion.button>
                            ))}
                          </div>
                        </div>

                        <div className="flex flex-wrap justify-center gap-3 mt-4">
                          <motion.button
                            onClick={() => setShowClearCartModal(true)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md"
                            aria-label="Clear cart"
                          >
                            Clear Cart
                          </motion.button>
                          <motion.button
                            onClick={handleCheckout}
                            disabled={isCheckingOut}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`gradient-button text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold shadow-md ${isCheckingOut ? "opacity-50 cursor-not-allowed" : ""}`}
                            aria-label={`Place order for ₹${total.toFixed(2)}`}
                          >
                            Place Order (₹{total.toFixed(2)})
                          </motion.button>
                        </div>
                      </>
                    )}
                  </motion.div>
                </div>
              </div>
            )}
          </section>

          {/* Receipt Modal */}
          {showReceipt && orderDetails && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="receipt-title"
            >
              <div
                className={`p-6 rounded-xl shadow-lg max-w-[90vw] sm:max-w-md w-full mx-2 bg-[var(--card-bg-light)] dark:bg-[var(--card-bg-dark)] text-[${darkMode ? "var(--text-dark)" : "var(--text-light)"}]}`}
              >
                <h3 id="receipt-title" className="text-lg font-semibold mb-4 text-[var(--primary)]">
                  Order Receipt #{orderDetails.id}
                </h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Date:</strong> {orderDetails.date}</p>
                  <p><strong>Delivery Address:</strong> {orderDetails.address}</p>
                  <p><strong>Payment Method:</strong> {orderDetails.paymentMethod}</p>
                  <h4 className="font-semibold mt-4">Items:</h4>
                  {orderDetails.items.map((item, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{item.name} (x{item.quantity})</span>
                      <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-gray-300 dark:border-gray-700 pt-2">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>₹{orderDetails.subtotal}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tax (5%):</span>
                      <span>₹{orderDetails.tax}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Delivery Fee:</span>
                      <span>₹{orderDetails.deliveryFee}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Discount:</span>
                      <span className="text-[var(--primary)]">-₹{orderDetails.discount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tip:</span>
                      <span className="text-[var(--primary)]">+₹{orderDetails.tip}</span>
                    </div>
                    <div className="flex justify-between font-semibold mt-2">
                      <span>Total:</span>
                      <span className="text-[var(--primary)]">₹{orderDetails.total}</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <motion.button
                    onClick={() => setShowReceipt(false)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="gradient-button text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold shadow-md"
                    aria-label="Close receipt"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          {editItem && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="edit-item-title"
            >
              <div
                className={`p-6 rounded-xl shadow-lg max-w-[90vw] sm:max-w-md w-full mx-2 bg-[var(--card-bg-light)] dark:bg-[var(--card-bg-dark)] text-[${darkMode ? "var(--text-dark)" : "var(--text-light)"}]}`}
              >
                <h3 id="edit-item-title" className="text-lg font-semibold mb-4 text-[var(--primary)]">
                  Edit {editItem.name}
                </h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <label className="block text-sm font-medium mb-1">Toppings</label>
                    <div className="flex flex-wrap gap-2">
                      {["Extra Sauce", "Onions", "Lettuce"].map((topping) => (
                        <label key={topping} className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={customizations.toppings[topping] || false}
                            onChange={(e) => {
                              setCustomizations((prev) => ({
                                ...prev,
                                toppings: {
                                  ...prev.toppings,
                                  [topping]: e.target.checked,
                                },
                              }));
                            }}
                            className="h-4 w-4 text-[var(--primary)] focus:ring-[var(--primary)]"
                            aria-label={`Toggle ${topping} topping`}
                          />
                          <span className="text-sm">{topping}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="spice-level-select" className="block text-sm font-medium mb-1">
                      Spice Level
                    </label>
                    <select
                      id="spice-level-select"
                      value={customizations.spiceLevel}
                      onChange={(e) => setCustomizations((prev) => ({ ...prev, spiceLevel: e.target.value }))}
                      className="input-field"
                      aria-label="Select spice level"
                    >
                      <option value="">Select...</option>
                      {["Mild", "Medium", "Spicy"].map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <motion.button
                    onClick={() => setEditItem(null)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`text-sm ${darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}
                    aria-label="Cancel edit"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    onClick={handleSaveCustomizations}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="gradient-button text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold shadow-md"
                    aria-label="Save customizations"
                  >
                    Save
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          {showClearCartModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="clear-cart-title"
            >
              <div
                className={`p-6 rounded-xl shadow-lg max-w-[90vw] sm:max-w-sm w-full mx-2 bg-[var(--card-bg-light)] dark:bg-[var(--card-bg-dark)] text-[${darkMode ? "var(--text-dark)" : "var(--text-light)"}]}`}
              >
                <h3 id="clear-cart-title" className="text-lg font-semibold mb-4 text-[var(--primary)]">
                  Clear Cart?
                </h3>
                <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"} mb-4`}>
                  Are you sure you want to clear all items from your cart?
                </p>
                <div className="flex justify-end gap-2">
                  <motion.button
                    onClick={() => setShowClearCartModal(false)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`text-sm ${darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}
                    aria-label="Cancel clear cart"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    onClick={() => {
                      clearCart();
                      setShowClearCartModal(false);
                      setToast({ message: "Cart cleared.", type: "success" });
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md"
                    aria-label="Confirm clear cart"
                  >
                    Clear
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          {showClearSavedModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="clear-saved-title"
            >
              <div
                className={`p-6 rounded-xl shadow-lg max-w-[90vw] sm:max-w-sm w-full mx-2 bg-[var(--card-bg-light)] dark:bg-[var(--card-bg-dark)] text-[${darkMode ? "var(--text-dark)" : "var(--text-light)"}]}`}
              >
                <h3 id="clear-saved-title" className="text-lg font-semibold mb-4 text-[var(--primary)]">
                  Clear Saved Items?
                </h3>
                <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"} mb-4`}>
                  Are you sure you want to clear all saved items?
                </p>
                <div className="flex justify-end gap-2">
                  <motion.button
                    onClick={() => setShowClearSavedModal(false)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`text-sm ${darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}
                    aria-label="Cancel clear saved items"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    onClick={handleClearSavedItems}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md"
                    aria-label="Confirm clear saved items"
                  >
                    Clear
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          {savedItems.length > 0 && (
            <section className="section-container max-w-4xl mx-auto mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[var(--primary)]">Saved for Later</h3>
                <motion.button
                  onClick={() => setShowClearSavedModal(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md"
                  aria-label="Clear all saved items"
                >
                  Clear All
                </motion.button>
              </div>
              <div className="grid gap-4">
                {savedItems.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`rounded-xl shadow-lg card-hover transition-all duration-300 bg-[var(--card-bg-light)] dark:bg-[var(--card-bg-dark)] p-4`}
                  >
                    <div className="flex flex-col sm:flex-row items-center justify-between">
                      <div className="mb-2 sm:mb-0">
                        <p className="text-base font-semibold truncate">{item.name}</p>
                        <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                          ₹{item.price.toFixed(2)}
                        </p>
                      </div>
                      <motion.button
                        onClick={() => handleAddBackToCart(item, i)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="gradient-button text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold shadow-md"
                        aria-label={`Add ${item.name} back to cart`}
                      >
                        Add to Cart
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>
          )}
        </main>

          {/* Footer */}
          <motion.footer
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className={`py-12 px-4 md:px-6 ${darkMode ? "bg-[var(--bg-dark)] text-[var(--text-dark)]" : "bg-[var(--bg-light)] text-[var(--text-light)]"} animate-slideIn`}
          role="contentinfo"
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h4 className="text-lg font-semibold mb-4 text-[var(--primary)]">About Zoomo Eats</h4>
                <p className="text-sm font-medium">
                  Zoomo Eats delivers your favorite meals from top restaurants in Jourian with speed and ease.
                </p>
                <div className="flex gap-4 mt-4">
                  {["facebook", "twitter", "instagram"].map((platform, i) => (
                    <motion.a
                      key={platform}
                      href={`https://${platform === "instagram" ? "www.instagram.com" : platform + ".com"}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.1, duration: 0.3 }}
                      whileHover={{ scale: 1.2 }}
                      className="text-[var(--primary)] hover:text-[var(--primary-dark)] text-lg"
                      aria-label={`Visit our ${platform} page`}
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path
                          d={
                            platform === "facebook"
                              ? "M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H7v-3h3V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"
                              : platform === "twitter"
                              ? "M22.46 6c-.77.35-1.6.58-2.46.69a4.3 4.3 0 001.88-2.38c-.83.5-1.75.85-2.72 1.05a4.28 4.28 0 00-7.28 3.9A12.15 12.15 0 013.15 4.78a4.27 4.27 0 001.32 5.71c-.69-.02-1.34-.21-1.9-.5v.05a4.28 4.28 0 003.43 4.19c-.63.17-1.29.26-1.97.26-.48 0-.95-.05-1.41-.15a4.28 4.28 0 003.99 2.97A8.58 8.58 0 012 18.62a12.08 12.08 0 006.55 1.92c7.86 0 12.15-6.51 12.15-12.15 0-.18 0-.37-.01-.55A8.7 8.7 0 0022.46 6z"
                              : "M12 2.04c-5.5 0-9.96 4.46-9.96 9.96 0 4.4 3.58 8.04 8.12 8.94v-6.32h-2.44v-2.62h2.44v-1.99c0-2.41 1.47-3.72 3.61-3.72 1.03 0 1.91 .08 2.17 .11v2.51h-1.49c-1.17 0-1.39 .56-1.39 1.37v1.79h2.78l-.36 2.62h-2.42v6.32c4.54-.9 8.12-4.54 8.12-8.94 0-5.5-4.46-9.96-9.96-9.96z"
                          }
                        />
                      </svg>
                    </motion.a>
                  ))}
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <h4 className="text-lg font-semibold mb-4 text-[var(--primary)]">Quick Links</h4>
                <ul className="text-sm font-medium space-y-2">
                  <li>
                    <Link
                      to="/"
                      className="hover:text-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-colors duration-300"
                      aria-label="Go to homepage"
                    >
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/faq"
                      className="hover:text-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-colors duration-300"
                      aria-label="View FAQ"
                    >
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/contact"
                      className="hover:text-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-colors duration-300"
                      aria-label="Contact us"
                    >
                      Contact
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/terms"
                      className="hover:text-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-colors duration-300"
                      aria-label="View Terms of Service"
                    >
                      Terms
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/privacy"
                      className="hover:text-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-colors duration-300"
                      aria-label="View Privacy Policy"
                    >
                      Privacy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/orders"
                      className="hover:text-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-colors duration-300"
                      aria-label="View Orders"
                    >
                      Orders
                    </Link>
                  </li>
                </ul>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <h4 className="text-lg font-semibold mb-4 text-[var(--primary)]">Stay Connected</h4>
                <p className="text-sm font-medium mb-4">Join our newsletter for exclusive deals!</p>
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-3">
                  <input
                    type="email"
                    placeholder="Enter email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    className="input-field"
                    aria-label="Newsletter email"
                    required
                  />
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="gradient-button text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 shadow-md"
                    aria-label="Subscribe to newsletter"
                  >
                    <FiMail className="w-5 h-5" /> Subscribe
                  </motion.button>
                </form>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <h4 className="text-lg font-semibold mb-4 text-[var(--primary)]">Support</h4>
                <motion.button
                  onClick={() => setShowSupport(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="gradient-button text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 shadow-md w-full"
                  aria-label="Open support chatbot"
                >
                  <FiMessageSquare className="w-5 h-5" /> Chat with Us
                </motion.button>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="text-center text-sm font-medium"
            >
              <p className="flex items-center justify-center gap-1 flex-wrap">
                © {new Date().getFullYear()} <span className="font-semibold text-[var(--primary)]">Zoomo Eats</span> — Designed with
                <span className="text-[var(--primary)] text-xl">
                  <AiFillHeart />
                </span>
                by Shiv
              </p>
            </motion.div>
          </div>
        </motion.footer>

        <Chatbot
          darkMode={darkMode}
          setToastMessage={setToast}
          isOpen={showSupport}
          onClose={() => setShowSupport(false)}
        />
      </div>
    </ErrorBoundary>
  );
};

export default CartPage;